import React, { useState } from 'react'
import ReactModal from 'react-modal'
import { useMutation } from '@apollo/client'
import Image from 'next/legacy/image'
import cn from 'classnames'
// Components
import Button from '@/components/newButton/Button'
import Radio from '@/components/newRadio/Radio'
import Select from '@/components/newSelect/Select'
// Utils
import useMediaQuery from '@/utils/publicApi'
import { countries, statesByCountry } from '@/utils/countries_and_states'
import { valPhone, valEmail } from '@/components/apply/lib/utils'
import { CREATE_REQUEST_USER } from '@/graphql/index'
import { getClickId } from '@/utils/clickId'
// Styles
import styles from './askAboutPuppyModal.module.scss'

export default function AskAboutPuppyModal({ isOpen, close, dataPuppy }) {
  const isMobile = useMediaQuery('(max-width:600px)')
  const isTablet = useMediaQuery('(max-width:1270px)')
  const FORM_INITIAL_STATE = {
    name: '',
    lastName: '',
    email: '',
    phone: '',
    comments: '',
    country: { label: '', value: '' },
    state: { label: '', value: '' },
    textOk: true,
  }
  const [data, setData] = useState(FORM_INITIAL_STATE)

  const [showError, setShowError] = useState(false)
  const [submittedData, setSubmittedData] = useState(false)

  const [submit] = useMutation(CREATE_REQUEST_USER, {
    onCompleted: (data) => {
      if (data?.createRequestAboutPuppy?.errorMessage) {
        console.error('ERROR', data?.createRequestAboutPuppy?.errorMessage)
      } else {
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'askAboutMe' })
        }
        setSubmittedData(true)
        setData(FORM_INITIAL_STATE)
        setShowError(false)
      }
    },
  })
  // Form validation
  const empty =
    data.name === '' ||
    data.lastName === '' ||
    data?.state?.value === '' ||
    data?.country?.value === '' ||
    data.phone === '' ||
    data.email === ''

  const isDisabled =
    data.name === '' ||
    data.lastName === '' ||
    data?.state?.value === '' ||
    data?.country?.value === '' ||
    !valPhone(data.phone) ||
    !valEmail(data.email)

  const handleSubmit = () => {
    // Validates not null fields
    if (isDisabled) {
      return setShowError(true)
    } else {
      const googleClickId = getClickId('gclid')
      const facebookClickId = getClickId('fbclid')

      return submit({
        variables: {
          request: {
            name: data?.name,
            lastName: data?.lastName,
            email: data?.email,
            phone: data?.phone,
            state: data.state.label,
            country: data?.country.value,
            comments: data?.comments,
            textOk: data?.textOk,
            dogId: dataPuppy?.id,
            googleClickId: googleClickId,
            facebookClickId: facebookClickId,
          },
        },
      })
    }
  }

  return (
    <ReactModal
      isOpen={isOpen}
      overlayClassName={styles.modalOverlay}
      className={styles.modal}
      onRequestClose={() => {
        close()
        setShowError(false)
      }}
    >
      <div
        className={styles.closeButton}
        onClick={() => {
          close()
          setData(FORM_INITIAL_STATE)
          setShowError(false)
        }}
      >
        <img src="/close-modal-grey.svg" alt="close icon" />
      </div>
      {!submittedData ? (
        <div className={styles.content}>
          {/* // VIEW FORM */}
          <section className={styles.image}>
            <div>
              <Image
                src={
                  dataPuppy?.profilePicture
                    ? process.env.NEXT_PUBLIC_MEDIA_BUCKET +
                      dataPuppy?.profilePicture?.small
                    : '/placeholder-dog.svg'
                }
                layout="responsive"
                objectFit="cover"
                loading="lazy"
                width={150}
                height={150}
                alt="dog"
              />
            </div>
            <div className={styles.info}>
              <h4 className={styles.name}> {dataPuppy?.name}</h4>
              <p className={styles.data}>
                {' '}
                {dataPuppy?.breed.name} | {dataPuppy?.gender}
              </p>
            </div>
          </section>
          <section className={styles.sectionForm}>
            <h3>Ask about {dataPuppy?.name}!</h3>
            <p>
              You can either <b>call</b> or <b>text</b> us at{' '}
              {isTablet ? <br /> : null}
              <a className={styles.link} href="tel:+1-888-488-7203">
                1-888-488-7203
              </a>
              , or fill out the form below so one of our puppy experts can
              contact you.
            </p>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputs}>
                <div className={styles.firstLine}>
                  <div className={styles.inputBox}>
                    <input
                      className={cn({
                        [styles.field]: true,
                        [styles.showError]: showError && data.name === '',
                      })}
                      type="text"
                      required="required"
                      value={data.name}
                      onChange={(e) =>
                        setData({ ...data, name: e.target.value })
                      }
                    />
                    <span
                      className={cn({
                        [styles.label]: true,
                        [styles.message]: showError && data.name === '',
                      })}
                    >
                      First Name
                    </span>
                    {showError && data.name === '' && (
                      <div className={styles.errorMessage}>
                        <div className={styles.icon} />
                        <div className={styles.error}>
                          <div className={styles.message}>
                            Please enter a first name
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.inputBox}>
                    <input
                      className={cn({
                        [styles.field]: true,
                        [styles.showError]: showError && data.lastName === '',
                      })}
                      type="text"
                      required="required"
                      value={data.lastName}
                      onChange={(e) =>
                        setData({ ...data, lastName: e.target.value })
                      }
                    />
                    <span
                      className={cn({
                        [styles.label]: true,
                        [styles.message]: showError && data.lastName === '',
                      })}
                    >
                      Last Name
                    </span>
                    {showError && data.lastName === '' && (
                      <div className={styles.errorMessage}>
                        <div className={styles.icon} />
                        <div className={styles.error}>
                          <div className={styles.message}>
                            Please enter a last name
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.firstLine}>
                  <div className={styles.inputBox}>
                    <input
                      className={cn({
                        [styles.field]: true,
                        [styles.showError]: showError && !valEmail(data.email),
                      })}
                      type="text"
                      required="required"
                      value={data.email}
                      onChange={(e) =>
                        setData({ ...data, email: e.target.value })
                      }
                    />
                    <span
                      className={cn({
                        [styles.label]: true,
                        [styles.message]: showError && !valEmail(data.email),
                      })}
                    >
                      Email
                    </span>
                    {showError && !valEmail(data.email) && (
                      <div className={styles.errorMessage}>
                        <div className={styles.icon} />
                        <div className={styles.error}>
                          <div className={styles.message}>
                            Please enter a valid email address
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.inputBox}>
                    <input
                      className={cn({
                        [styles.field]: true,
                        [styles.showError]: showError && !valPhone(data.phone),
                      })}
                      type="text"
                      required="required"
                      value={data.phone}
                      onChange={(e) =>
                        setData({ ...data, phone: e.target.value })
                      }
                    />
                    <span
                      className={cn({
                        [styles.label]: true,
                        [styles.message]: showError && !valPhone(data.phone),
                      })}
                    >
                      Phone
                    </span>
                    {showError && !valPhone(data.phone) && (
                      <div className={styles.errorMessage}>
                        <div className={styles.icon} />
                        <div className={styles.error}>
                          <div className={styles.message}>
                            Please enter a valid phone number
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.firstLine}>
                  <div className={styles.inputBox}>
                    <Select
                      options={countries}
                      placeholder="Country"
                      value={data.country}
                      emptyPlaceholder="Please select a country"
                      onChange={(country) => {
                        setData({
                          ...data,
                          country,
                        })
                      }}
                      showError={showError && !data?.country.value}
                      errorMessage={showError && 'Please select a country'}
                      newDesign
                    />
                  </div>
                  <div className={styles.inputBox}>
                    <Select
                      options={
                        statesByCountry.find(
                          (s) => s?.value === data?.country?.value,
                        )?.states || []
                      }
                      placeholder="State/Province"
                      value={data.state}
                      emptyPlaceholder="Please select a country"
                      disabled={!data?.country?.value}
                      onChange={(state) =>
                        setData({
                          ...data,
                          state,
                        })
                      }
                      showError={showError && !data?.state.value}
                      errorMessage={
                        showError && 'Please select a state or province'
                      }
                      newDesign
                    />
                  </div>
                </div>
                <div className={styles.inputBox}>
                  <textarea
                    className={
                      data.comments.length > 1 ? styles.focus : styles.textarea
                    }
                    placeholder="Any comments? The more information you provide, the better we can assist you in finding your perfect furry companion."
                    value={data.comments}
                    onChange={(e) =>
                      setData({ ...data, comments: e.target.value })
                    }
                  />
                </div>
                <div className={styles.textMe}>
                  <Radio
                    inline
                    options={[{ value: true, label: 'It is OK to message me' }]}
                    value={data.textOk}
                    onChange={() =>
                      setData({
                        ...data,
                        textOk: !data.textOk,
                      })
                    }
                  />
                </div>

                <div className={styles.privacy}>
                  {' '}
                  We value your privacy. View our{' '}
                  <a target="_blank" href="/tos" className={styles.link}>
                    Terms & Conditions
                  </a>{' '}
                  and{' '}
                  <a target="_blank" href="/policy" className={styles.link}>
                    Privacy Policy
                  </a>
                  .
                </div>
              </div>
              <div className={styles.btnSection}>
                <Button
                  title="Send"
                  onClick={handleSubmit}
                  theme={
                    empty ? ['medium', 'grey-solid'] : ['medium', 'green-solid']
                  }
                />
              </div>
            </form>
          </section>
        </div>
      ) : (
        <div className={styles.content}>
          {/* // VIEW SENDING REQUEST */}
          <section className={styles.image2}>
            <div>
              {' '}
              <Image
                src={
                  dataPuppy?.profilePicture
                    ? process.env.NEXT_PUBLIC_MEDIA_BUCKET +
                      dataPuppy?.profilePicture?.small
                    : '/placeholder-dog.svg'
                }
                layout="responsive"
                objectFit="cover"
                loading="lazy"
                width={150}
                height={isMobile ? 120 : 150}
                alt="dog"
              />
            </div>
          </section>
          <section className={styles.form}>
            <section className={styles.finalMessage}>
              <h3>
                Thank you for contacting us about {!isMobile && <br />}
                {dataPuppy?.name}!
              </h3>
              <p>
                We're wagging our tails with excitement to get back to you about
                our puppies. Expect to hear from us soon!
              </p>
              <div className={styles.btnSection}>
                <Button
                  title="Close"
                  onClick={() => {
                    close()
                    setSubmittedData(false)
                  }}
                  theme={['medium', 'green-solid']}
                />
              </div>
            </section>
          </section>
        </div>
      )}
    </ReactModal>
  )
}
